import {EnkelGruppeModell, GruppeId, MøteAggregaterModell, MøteModellEnkel, ÅrshjulInnslagModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {gruppefarge, Gruppefarge} from "felles-ng2/modell/Gruppefarge";
import {CelleInfo, Gruppedata, Segmentring, ÅrshjulGraf} from "./ÅrshjulGraf";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";

export class ÅrshjulModell {

    foerstkommendeMoete: MøteModellEnkel | null = null;
    valgtGruppe: EnkelGruppeModell | null = null;
    valgtMoete: MøteModellEnkel | null = null;
    valgtMoeteAggregater: MøteAggregaterModell | null = null;
    valgtAarshjulinnslag: ÅrshjulInnslagModell | null = null;
    valgtInfoKontainerPosisjonStil: HtmlDimensjon | null = null;
    valgtInfoKontainerHoyde = 0;
    svgKontainerDimensjonStil: { width: string; height: string } | null = null;

    visHjelpetekst = false;
    hjelptekstPosisjonStil: HtmlPosisjon | null = null;
    musoverCelle: CelleInfoMedData | null = null;

    utelatteGrupper: Gruppedata[] = [];

    private grupper: Gruppeinfo[] = [];
    private periode: ZonedDateTimePeriodeLukket | null = null;

    private årshjulinfoLasterCache!: MemoisertFunksjon<Promise<ÅrshjulInnslagModell[]>>;
    private møterLasterCache!: MemoisertFunksjon<Promise<MøteModellEnkel[]>>;

    private møteringHøyde = 50;
    private årshjulinnslagHøyde = 20;
    private årshjulinnslagHøydeRedusert = 14;
    private skaleringsfaktor = 1;

    constructor(
      private oversettelser: Oversettelser,
      private markForCheck: () => void,
      //@todo burde last for multiple grupper
      årshjulinfoLaster: (gruppeId: GruppeId) => Promise<ÅrshjulInnslagModell[]>,
      møteLaster: (gruppeId: GruppeId, periode: ZonedDateTimePeriodeLukket) => Promise<MøteModellEnkel[]>
    ) {
        this.årshjulinfoLasterCache = memoiserEttArg(årshjulinfoLaster);
        this.møterLasterCache = memoiserEttArg((gruppeId: string) => møteLaster(gruppeId, this.periode!));
    }

    /** Returnerer en ÅrshjulModell populert med angitte årshjulinnslag og møter. */
    static opprettFraÅrshjulinnslagOgMøter(årshjulinnslag: ÅrshjulInnslagModell[], møter: MøteModellEnkel[], oversettelser: Oversettelser): ÅrshjulModell {
        return new ÅrshjulModell(
          oversettelser,
          () => {
          },
          (gruppeId) => Promise.resolve(årshjulinnslag),
          (gruppeId, periode) => Promise.resolve(møter)
        );
    }

    setGrupper(grupper: EnkelGruppeModell[]): ÅrshjulModell {
        this.grupper = grupper.map(g => (<Gruppeinfo>{
            gruppe: g,
            farger: gruppefarge(g),
            tittel: this.oversettelser.oversett(i18n('{moetenavn} i {grptittel} i {orgnavn}'), {moetenavn: g.moetenavn, grptittel: g.tittel, orgnavn: g.organisasjonsnavn})
        }));
        return this;
    }

    setPeriode(periode: ZonedDateTimePeriodeLukket): ÅrshjulModell {
        this.periode = periode;
        this.møterLasterCache.cache.clear();
        return this;
    }

    setUtelatteGrupper(utelatteGrupper: Gruppedata[]) {
        this.utelatteGrupper = utelatteGrupper;
    }

    dataForTommeRinger(): Gruppedata[] {
        return this.grupper.map(gruppe => ({
            gruppeId: gruppe.gruppe.id as string,
            gruppetittel: gruppe.tittel,
            ringer: [
                this.ringMedÅrsplaninnslag(1, gruppe, false, []),
                this.ringMedMøter(gruppe, [])]
        }));
    }

    dataForAlleMøterOgÅrshjulRinger(): Promise<Gruppedata[]> {
        const promiser: Promise<Gruppedata>[] = this.grupper
          .filter(gruppe => !this.utelatteGrupper.some(u => u.gruppeId === (gruppe.gruppe.id as string)))
          .map(gruppe => Promise.all([
              this.årshjulRinger(gruppe.gruppe.id as string),
              this.møteRinger(gruppe.gruppe.id as string)])
            .then(ringer => ({gruppeId: gruppe.gruppe.id as string, gruppetittel: gruppe.tittel, ringer: ringer.flat()})));
        return Promise.all(promiser);
    }

    dataForAlleÅrshjulRinger(): Promise<Gruppedata[]> {
        const promiser: Promise<Gruppedata>[] = this.grupper
          .filter(gruppe => !this.utelatteGrupper.some(u => u.gruppeId === (gruppe.gruppe.id as string)))
          .map(gruppe => Promise.all([
              this.årshjulRinger(gruppe.gruppe.id as string)])
            .then(ringer => ({gruppeId: gruppe.gruppe.id as string, gruppetittel: gruppe.tittel, ringer: ringer.flat()})));
        return Promise.all(promiser);
    }

    årshjulRinger(gruppeId: string): Promise<Segmentring[]> {
        return this.årshjulinfoLasterCache(gruppeId)
          .then((innslag: ÅrshjulInnslagModell[]) => {
              const kollisjonsfrieInnslag = tilKolisjonsfrieÅrshjulinnslagsamlinger(innslag);
              return kollisjonsfrieInnslag
                .reverse()
                .map((innslagSamling, i) => this.ringMedÅrsplaninnslag(i, this.finnGruppe(gruppeId), kollisjonsfrieInnslag.length > 1, innslagSamling));
          });
    }

    møteRinger(gruppeId: string): Promise<Segmentring[]> {
        return this.møterLasterCache(gruppeId)
          .then(innslag => [this.ringMedMøter(this.finnGruppe(gruppeId), innslag)]);
    }

    nyttÅrshjulinnslag(innslag: ÅrshjulInnslagModell): ÅrshjulModell {
        this.årshjulinfoLasterCache(innslag.gruppeId as string)
          .then(samling => samling.push(innslag));
        return this;
    }

    oppdatertÅrshjulinnslag(innslag: ÅrshjulInnslagModell): ÅrshjulModell {
        this.årshjulinfoLasterCache(innslag.gruppeId as string)
          .then(samling => Object.assign(samling.find(i => i.id === innslag.id)!, innslag));
        return this;
    }

    slettetÅrshjulinnslag(innslag: ÅrshjulInnslagModell): ÅrshjulModell {
        this.årshjulinfoLasterCache(innslag.gruppeId as string)
          .then(samling => {
              const idx = samling.findIndex(i => i.id === innslag.id);
              if (idx !== -1) samling.splice(idx, 1);
          });
        return this;
    }

    posisjonerValgtCelleKontainer(årshjulGraf: any/*ÅrshjulGraf*/): ÅrshjulModell {
        const årshjuldiameter = årshjulGraf.ytreDiameterAbsolutt();
        const indreRadius = årshjulGraf.indreDiameterAbsolutt() / 2;
        const hb = Math.sqrt(2 * (Math.pow(indreRadius, 2)));
        const xy = (årshjuldiameter - hb) / 2;
        this.valgtInfoKontainerPosisjonStil = {left: `${xy}px`, top: `${xy}px`, width: `${hb}px`, height: `${hb}px`};
        this.valgtInfoKontainerHoyde = hb;
        return this;
    }

    setValgtMoete(møte: MøteModellEnkel, lastMøteaggregater: (møteId: string) => Promise<MøteAggregaterModell>, lastGruppe: (gruppeId: string) => EnkelGruppeModell): ÅrshjulModell {
        this.resetValgtElement();
        this.valgtMoete = møte;
        this.valgtGruppe = lastGruppe(møte.eiendeGruppeId as string);
        lastMøteaggregater(møte.id as string).then(aggregater => {
            this.valgtMoeteAggregater = aggregater;
            this.markForCheck();
        }); //@todo trengs markForCheck?
        return this;
    }

    setValgtÅrshjulinnslag(årshjulInnslag: ÅrshjulInnslagModell): ÅrshjulModell {
        this.resetValgtElement();
        this.valgtAarshjulinnslag = årshjulInnslag;
        this.valgtGruppe = this.finnGruppe(årshjulInnslag.gruppeId as string).gruppe;
        return this;
    }

    resetValgtElement(): ÅrshjulModell {
        this.valgtMoete = null;
        this.valgtAarshjulinnslag = null;
        return this;
    }

    setHjelpetekstForCelle(celle: CelleInfoMedData, pos: HtmlPosisjon) {
        this.visHjelpetekst = true;
        this.hjelptekstPosisjonStil = pos;
        this.musoverCelle = celle;
    }

    resetHjelpetekstForCelle() {
        this.visHjelpetekst = false;
    }

    zoomTilAbsolutt(skaleringsfaktor: number, tilgjengeligHøyde: number) {
        this.skaleringsfaktor = skaleringsfaktor;
        this.svgKontainerDimensjonStil = {width: `${tilgjengeligHøyde}px`, height: `${tilgjengeligHøyde}px`};
    }

    zoomTil(skaleringsfaktor: number) {
        this.skaleringsfaktor = skaleringsfaktor;
        this.oppdaterSvgKontainerDimensjonStil();
    }

    zoomInn() {
        if (this.skaleringsfaktor >= 1) return;
        this.skaleringsfaktor = Math.round((this.skaleringsfaktor + 0.1) * 10) / 10;
        this.oppdaterSvgKontainerDimensjonStil();
    }

    zoomUt() {
        if (this.skaleringsfaktor <= 0.3) return;
        this.skaleringsfaktor = Math.round((this.skaleringsfaktor - 0.1) * 10) / 10;
        this.oppdaterSvgKontainerDimensjonStil();
    }

    skaleringsfaktorSomProsent(): number {
        return Math.floor(this.skaleringsfaktor * 100);
    }

    private finnGruppe(gruppeId: string): Gruppeinfo {
        return this.grupper.find(g => g.gruppe.id === gruppeId)!;
    }

    private ringMedMøter(gruppe: Gruppeinfo, møter: MøteModellEnkel[]): Segmentring {
        return {
            id: `ring_møter_${gruppe.gruppe.id}`,
            cssklasse: 'møtering e2e-møtering',
            farger: gruppe.farger,
            høyde: this.møteringHøyde,
            bakgrunnstekst: møter.length === 0 ? this.oversettelser.oversett(i18n('Ingen møter - klikk på ringen for å opprette')) : null,
            uker: this.ukerMedMøter(gruppe.gruppe.id as string, møter)
        };
    }

    private ukerMedMøter(gruppeId: string, møter: MøteModellEnkel[]): CelleInfoMedData[] {
        const møterPerUke: Record<string, MøteModellEnkel[]> = {};
        for (const møte of møter) {
            const uke = ÅrshjulGraf.ukenummer(møte.dato.start).toString();
            (møterPerUke[uke] ??= []).push(møte);
        }
        return ÅrshjulGraf.ukedata()
          .map(d => this.populerCelleMedMøteinfo(d, gruppeId, møterPerUke[d.ukenummer.toString()]));
    }

    private populerCelleMedMøteinfo(celle: CelleInfo, gruppeId: string, møte: MøteModellEnkel[] | undefined): CelleInfoMedData {
        celle.tittel = '';
        celle.ekstra = {gruppeId, type: 'møtetom'};
        if (!Array.isArray(møte)) return <CelleInfoMedData>celle;
        celle.id = møte[0].id as string;
        celle.tittel = `${møte[0].nummer} ${møte[0].tittel}`;
        celle.ekstra = {gruppeId, type: 'møte', entitet: møte[0]};
        return <CelleInfoMedData>celle;
    }

    private ringMedÅrsplaninnslag(indeks: number, gruppe: Gruppeinfo, redusertHøyde: boolean, innslag: ÅrshjulInnslagModell[]): Segmentring {
        return {
            id: `ring_årshjul_${gruppe.gruppe.id}_${indeks}`,
            cssklasse: 'e2e-årshjulinnslagring',
            farger: gruppe.farger,
            høyde: redusertHøyde ? this.årshjulinnslagHøydeRedusert : this.årshjulinnslagHøyde,
            bakgrunnstekst: innslag.length === 0 ? this.oversettelser.oversett(i18n('Ingen årshjulinnslag - klikk her for å opprette')) : null,
            uker: this.ukerMedÅrshjulInnslag(gruppe.gruppe.id as string, innslag)
        };
    }

    private ukerMedÅrshjulInnslag(gruppeId: string, innslag: ÅrshjulInnslagModell[]): CelleInfoMedData[] {
        const uker = ÅrshjulGraf.ukedata()
          .map(d => this.populerCelleMedÅrshjulinnslag(d, gruppeId, null));
        let offset = 0;
        [...innslag].sort((a, b) => a.startuke - b.startuke).forEach((i: ÅrshjulInnslagModell) => {
            const idx = i.startuke - 1 - offset;
            uker[idx] = this.populerCelleMedÅrshjulinnslag(uker[idx], gruppeId, i);
            if (i.antallUker > 1) {
                const antall = i.antallUker - 1;
                const fjernede = uker.splice(idx + 1, antall);
                const antallOverskytende = fjernede.filter(x => x == null).length;
                if (antallOverskytende > 0) uker.splice(0, antallOverskytende);
                offset += antall;
            }
        });
        return uker;
    }

    private populerCelleMedÅrshjulinnslag(celle: CelleInfo, gruppeId: string, innslag: ÅrshjulInnslagModell | null): CelleInfoMedData {
        celle.tittel = '';
        celle.ekstra = {gruppeId, type: 'årshjulinnslagtom'};
        if (innslag == null) return <CelleInfoMedData>celle;
        celle.id = innslag.id as string;
        celle.tittel = innslag.tittel as string;
        celle.bredde = innslag.antallUker;
        celle.ekstra = {gruppeId, type: 'årshjulinnslag', entitet: innslag};
        return <CelleInfoMedData>celle;
    }

    private oppdaterSvgKontainerDimensjonStil() {
        this.svgKontainerDimensjonStil = {
            width: `${this.skaleringsfaktor * 100}%`,
            height: `${this.skaleringsfaktor * 100}%`
        };
    }
}


type MemoisertFunksjon<T> = ((key: string) => T) & { cache: Map<string, T> };

function memoiserEttArg<T>(fn: (key: string) => T): MemoisertFunksjon<T> {
    const cache = new Map<string, T>();
    const memoized: any = (key: string) => {
        if (!cache.has(key)) cache.set(key, fn(key));
        return cache.get(key)!;
    };
    memoized.cache = cache;
    return memoized;
}

export function tilKolisjonsfrieÅrshjulinnslagsamlinger(alleInnslag: ÅrshjulInnslagModell[]): ÅrshjulInnslagModell[][] {
    const kollisjonsfrie: ÅrshjulInnslagModell[][] = [[]];
    [...alleInnslag].sort((a, b) => a.startuke - b.startuke).forEach(innslag => {
        let i = 0;
        while (girKollisjon(innslag, kollisjonsfrie[i++])) {
            if (kollisjonsfrie.length <= i) kollisjonsfrie.push([]);
        }
        kollisjonsfrie[i - 1].push(innslag);
    });
    return kollisjonsfrie;
}

function girKollisjon(kandidat: ÅrshjulInnslagModell, måltabell: ÅrshjulInnslagModell[]): boolean {
    if (overlapper(kandidat, måltabell[måltabell.length - 1]!)) return true;
    if (kandidat.startuke + kandidat.antallUker - 1 > 52)
        return overlapper({startuke: 1, antallUker: kandidat.startuke + kandidat.antallUker - 1 - 52}, måltabell[0]!);
    return false;
}

function overlapper(a: { startuke: number; antallUker: number }, b: { startuke: number; antallUker: number }): boolean {
    if (a == null || b == null) return false;
    if (b.antallUker > a.antallUker) {
        const a_ = a;
        a = b;
        b = a_;
    }
    const aSlutt = a.startuke + a.antallUker - 1;
    const bSlutt = b.startuke + b.antallUker - 1;
    return (b.startuke >= a.startuke && b.startuke <= aSlutt)
      || (bSlutt >= a.startuke && bSlutt <= aSlutt);
}

export interface CelleInfoMedData extends CelleInfo {
    ekstra: {
        gruppeId: string;
        type: 'møte' | 'møtetom' | 'årshjulinnslag' | 'årshjulinnslagtom';
        entitet?: MøteModellEnkel | ÅrshjulInnslagModell;
    }
}

export interface HtmlPosisjon {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    transformOrigin?: string;
}

export interface HtmlDimensjon {
    top: string;
    left: string;
    width: string;
    height: string;
}

interface Gruppeinfo {
    gruppe: EnkelGruppeModell;
    farger: Gruppefarge;
    tittel: string;
}
